/**
 * Evidence library — /w/:slug/app/evidence
 *
 * E4 of docs/evidence-model-plan.md. The customer's per-profile catalogue
 * of typed evidence artifacts (policies, technical controls, monitoring
 * links, process records, freeform notes). Reusable across register items
 * via bindings; the register-item detail Evidence section (E3) reads the
 * same store.
 *
 * v1 surfaces:
 *   - list + filter by type
 *   - upload (multipart PDF/DOCX/MD/TXT) → creates a policy artifact
 *   - link (URL) → creates a monitoring/technical/process/freeform artifact
 *   - delete (refused with the binding list when still bound)
 *
 * PR A (V102-8-2): ?program=<key>&period=<id>&need=unassessed | &filter=stale
 * opens a program focus above the library (CmmcEvidenceFocus): what the
 * scorecard's Collect / Review stale point at, with the same counts.
 *
 * Deferred to v1.1+:
 *   - inline metadata edit (title/version/cadence)
 *   - artifact detail modal with full "where used" list + attach-to-item
 *   - live-query integrations (E10)
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useDialogMobileOnly } from '../lib/use-dialog-mobile-only.js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { fmtDate } from '../lib/format';
import {
  getUnifiedEvidence,
  getEngagementEvidence,
  req,
  uploadEvidenceForTest,
  type UnifiedEvidenceItem,
  type EngagementEvidenceRow,
} from '../lib/api';
import { useInvalidate } from '../lib/query-keys';
import { CmmcEvidenceFocus } from '../components/programs/CmmcEvidenceFocus';
import { UploadForObjective } from '../components/programs/CmmcAssess';
import '../components/couldnt-check.css';

type EvidenceType = 'policy' | 'technical' | 'monitoring' | 'process' | 'freeform';
type SourceFilter = 'all' | 'manual' | 'connector';
type EvidenceMode = 'register' | 'engagement';

const SOURCE_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: 'all', label: 'All evidence' },
  { value: 'manual', label: 'Manual uploads' },
  { value: 'connector', label: 'Connector checks' },
];

const KIND_WORD: Record<string, string> = { artifact: 'document', document: 'linked document', check: 'connector check' };

export function Evidence() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const canManage =
    workspaceCtx?.role === 'owner' ||
    workspaceCtx?.role === 'admin' ||
    workspaceCtx?.role === 'editor';

  const [items, setItems] = useState<UnifiedEvidenceItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  // V96-4-9: SOC 2 "Add evidence" deep-links with ?new=upload.
  const [searchParams] = useSearchParams();
  // V102-8-7: engagement mode groups evidence by (test × asset × period) for a C3PAO reviewer.
  const [mode, setMode] = useState<EvidenceMode>(
    searchParams.get('evidence') === 'engagement' ? 'engagement' : 'register',
  );
  const engagementTest = searchParams.get('test');
  const [showNew, setShowNew] = useState<'upload' | 'link' | null>(!canManage ? null : searchParams.get('new') === 'upload' ? 'upload' : searchParams.get('new') === 'link' ? 'link' : null);

  const invalidate = useInvalidate();
  const focusProgram = searchParams.get('program');
  const focusPeriod = searchParams.get('period');
  const focusItem = searchParams.get('item');
  const focusKind = searchParams.get('kind');
  // R4: a deep link that names no listed row says so (and takes focus) instead of leaving focus on the page.
  const itemMissing = !!focusItem && !!items && !items.some((i) => i.id === focusItem);
  useEffect(() => {
    if (!focusItem || !items) return;
    const el = document.getElementById(itemMissing ? 'ev-missing' : `ev-${focusItem}`);
    el?.scrollIntoView?.({ block: 'center' });
    el?.focus();
  }, [focusItem, items, itemMissing]);
  const focusMode = searchParams.get('filter') === 'stale' ? 'stale' as const : searchParams.get('need') === 'unassessed' ? 'unassessed' as const : searchParams.get('cited') ? 'cited' as const : null;
  // F19 / F21: where to go back to (only a page in this workspace), and what an upload is for.
  const navigate = useNavigate();
  const rawReturn = searchParams.get('return');
  const returnTo = slug && rawReturn && rawReturn.startsWith(`/w/${slug}/app/`) ? rawReturn : null;
  const uploadFor = (searchParams.get('for') ?? '').slice(0, 120) || null;
  const focused = !!(focusProgram && focusPeriod && focusMode);
  // V109-1-19 R3: an upload for a test GAC Pro couldn't check, or for one objective of a program period.
  const attachTest = searchParams.get('attach') ? (searchParams.get('test') ?? '').slice(0, 200) || null : null;
  const objectiveParam = searchParams.get('objective');
  const attachObjective = objectiveParam && focusProgram && focusPeriod ? { id: objectiveParam.slice(0, 200), program: focusProgram, periodId: focusPeriod } : null;

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getUnifiedEvidence(slug, {
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
      });
      setItems(data.evidence);
      setTotal(data.total);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Network error loading evidence.';
      if (msg.includes('404') || msg.includes('Not found')) {
        setError('This workspace has no profile yet. Complete onboarding to enable Evidence.');
      } else {
        setError(msg);
      }
      setItems(null);
    } finally {
      setLoading(false);
    }
  }, [slug, sourceFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!slug) return null;

  return (
    // T8R3-1: minmax(0, 1fr) so a long value never widens the page past the viewport.
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '72rem', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <PageHeader
        title="Evidence library"
        subtitle="Manual uploads and connector-collected checks in one view."
        right={
          canManage ? (
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button
                variant={showNew === 'link' ? 'secondary' : 'primary'}
                onClick={() => setShowNew(showNew === 'link' ? null : 'link')}
              >
                {showNew === 'link' ? 'Cancel' : 'Add link'}
              </Button>
              <Button
                variant={showNew === 'upload' ? 'secondary' : 'primary'}
                onClick={() => setShowNew(showNew === 'upload' ? null : 'upload')}
              >
                {showNew === 'upload' ? 'Cancel' : 'Upload policy'}
              </Button>
            </div>
          ) : null
        }
      />

      {/* V102-8-7: register / engagement view toggle. Register is the default; engagement
          groups by (test × asset × period) for a C3PAO or SOC 2 reviewer. */}
      <EvidenceModeToggle mode={mode} onChange={setMode} />

      {mode === 'engagement' ? (
        <EngagementView slug={slug} initialTestKey={engagementTest} />
      ) : null}

      {/* V109-1-19 R3 (R2-H3 / Medium): an upload for one objective or one test GAC Pro couldn't check
          (`?attach=1&test=…`, `?objective=…&program=…&period=…`) — pre-filled, never the generic form. */}
      {mode === 'register' && (attachTest || attachObjective) ? (
        canManage
          ? <AttachEvidence slug={slug} testKey={attachTest} objective={attachObjective} forLabel={uploadFor} returnTo={returnTo} />
          : <Alert variant="info">{'An owner, admin or editor adds evidence here. Send them this page’s link.'}</Alert>
      ) : null}

      {focused && mode === 'register' ? <CmmcEvidenceFocus slug={slug} program={focusProgram!} periodId={focusPeriod!} mode={focusMode!} cited={searchParams.get('cited')} canUpload={canManage} returnTo={returnTo} /> : null}

      {mode === 'register' && itemMissing ? (
        <div id="ev-missing" tabIndex={-1} data-part="item-missing">
          <Alert variant="warning">{`This item isn’t in Evidence · ${KIND_WORD[focusKind ?? ''] ?? 'evidence'}`}</Alert>
        </div>
      ) : null}

      {/* F20: a program focus hides the library-wide re-attestation banner. */}
      {mode === 'register' && !focused ? <NudgeBanner slug={slug} canDismiss={canManage} /> : null}

      {mode === 'register' && error ? <Alert variant="error">{error}</Alert> : null}

      {mode === 'register' && showNew === 'link' ? (
        <NewLinkForm slug={slug} onCreated={() => { setShowNew(null); void load(); if (slug) invalidate('evidence:changed', slug); }} />
      ) : null}
      {mode === 'register' && showNew === 'upload' ? (
        <NewUploadForm slug={slug} forLabel={uploadFor} onCreated={() => { setShowNew(null); void load(); if (slug) invalidate('evidence:changed', slug); if (returnTo && uploadFor) navigate(returnTo); }} />
      ) : null}

      {/* F20: in a program focus the general library sits behind "All evidence". */}
      {mode === 'register' ? (
      <LibraryWrap collapsed={focused} count={items ? total : null}>
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
        <Field id="source-filter" label="Source">
          <Select
            id="source-filter"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
          {items ? `${total} item${total === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {loading ? (
        <Spinner label="Loading evidence" />
      ) : !items || items.length === 0 ? (
        <EmptyState
          title="No evidence yet"
          description={
            canManage
              ? 'Upload a policy PDF, add a monitoring URL, or connect an evidence connector to start collecting checks automatically.'
              : 'Ask a workspace admin or editor to add evidence or configure connectors.'
          }
        />
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'minmax(0, 1fr)' }}>
          {items.map((item) => (
            <UnifiedEvidenceCard key={`${item.source_type}-${item.id}`} item={item} focused={item.id === focusItem} />
          ))}
        </ul>
      )}
      </LibraryWrap>
      ) : null}
    </div>
  );
}

// ============================================================
// V102-8-7 — Engagement view
// ============================================================

function EvidenceModeToggle({ mode, onChange }: { mode: EvidenceMode; onChange: (m: EvidenceMode) => void }) {
  const tabs: Array<{ id: EvidenceMode; label: string; hint: string }> = [
    { id: 'register', label: 'Register view', hint: 'Policies, uploads and connector checks in one list.' },
    { id: 'engagement', label: 'Engagement view', hint: 'Grouped by test, asset and audit period for a reviewer.' },
  ];
  return (
    <div role="tablist" aria-label="Evidence view" style={{
      display: 'inline-flex',
      padding: '2px',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-chip)',
      background: 'var(--surface-alt, var(--surface))',
      alignSelf: 'start',
      gap: '2px',
    }}>
      {tabs.map((t) => {
        const active = t.id === mode;
        return (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={active}
            title={t.hint}
            onClick={() => onChange(t.id)}
            style={{
              padding: 'var(--space-1) var(--space-3)',
              borderRadius: 'var(--radius-chip)',
              border: 'none',
              background: active ? 'var(--accent-soft, var(--surface))' : 'transparent',
              color: active ? 'var(--accent)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-3xs)',
              fontFamily: 'var(--font-ui)',
              fontWeight: 500,
              cursor: 'pointer',
              minHeight: '32px',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

const STATUS_STYLE: Record<EngagementEvidenceRow['status'], { bg: string; fg: string; label: string }> = {
  passing: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', label: 'Passing' },
  failing: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)', label: 'Failing' },
  stale: { bg: 'var(--warning-bg)', fg: 'var(--warning-text, var(--warning-fg))', label: 'Stale' },
  no_evidence: { bg: 'var(--surface-alt, var(--surface))', fg: 'var(--text-dim)', label: 'No evidence' },
};

function EngagementView({ slug, initialTestKey }: { slug: string; initialTestKey: string | null }) {
  const [testKey, setTestKey] = useState<string>(initialTestKey ?? '');
  const [periodId, setPeriodId] = useState<string>('');
  const [verify, setVerify] = useState<EngagementEvidenceRow | null>(null);

  const [data, setData] = useState<Awaited<ReturnType<typeof getEngagementEvidence>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    getEngagementEvidence(slug, {
      test: testKey || undefined,
      period: periodId || undefined,
    })
      .then((d) => {
        if (!cancel) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancel) setError(e instanceof Error ? e.message : 'Failed to load engagement evidence');
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [slug, testKey, periodId]);

  const rowsByAsset = useMemo(() => {
    const groups = new Map<string, { asset_name: string; asset_category: string; rows: EngagementEvidenceRow[] }>();
    for (const r of data?.rows ?? []) {
      const g = groups.get(r.asset_id) ?? { asset_name: r.asset_name, asset_category: r.asset_category, rows: [] };
      g.rows.push(r);
      groups.set(r.asset_id, g);
    }
    return [...groups.entries()].sort((a, b) => a[1].asset_name.localeCompare(b[1].asset_name));
  }, [data]);

  return (
    <section
      aria-label="Engagement evidence"
      style={{ display: 'grid', gap: 'var(--space-3)' }}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(260px, 2fr) minmax(180px, 1fr)',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}>
        <Field id="eng-test" label="Test">
          <Select id="eng-test" value={testKey} onChange={(e) => setTestKey(e.target.value)}>
            <option value="">All tests</option>
            {(data?.tests ?? []).map((t) => (
              <option key={t.test_key} value={t.test_key}>{t.title} — {t.test_key}</option>
            ))}
          </Select>
        </Field>
        <Field id="eng-period" label="Period">
          <Select id="eng-period" value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            <option value="">All periods</option>
            {(data?.periods ?? []).map((p) => (
              <option key={p.period_id} value={p.period_id}>{p.label}</option>
            ))}
          </Select>
        </Field>
      </div>

      {error ? <Alert variant="error">{error}</Alert> : null}
      {loading ? <Spinner label="Loading engagement evidence" /> : null}

      {!loading && (!data || data.rows.length === 0) ? (
        <EmptyState
          title="No evidence for this filter"
          description="Pick a different test or period, or clear the filter to see every (test × asset × period) tuple with its latest observation."
        />
      ) : null}

      {!loading && data && data.rows.length > 0 ? (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {rowsByAsset.map(([assetId, group]) => (
            <article
              key={assetId}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
                background: 'var(--surface)',
                overflow: 'hidden',
              }}
            >
              <header style={{
                padding: 'var(--space-2) var(--space-4)',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-alt, var(--surface-elevated))',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>{group.asset_name}</span>
                  <Badge>{group.asset_category}</Badge>
                </div>
                <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                  {group.rows.length} tuple{group.rows.length === 1 ? '' : 's'}
                </span>
              </header>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {group.rows.map((r) => {
                  const style = STATUS_STYLE[r.status];
                  return (
                    <li
                      key={`${r.test_key}-${r.asset_id}-${r.period_id}`}
                      style={{
                        padding: 'var(--space-3) var(--space-4)',
                        borderBottom: '1px solid var(--border)',
                        display: 'grid',
                        gridTemplateColumns: 'minmax(240px, 2fr) minmax(120px, 1fr) auto auto',
                        gap: 'var(--space-3)',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
                        <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text)', fontWeight: 500 }}>
                          {r.test_title}
                        </span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                          {r.test_key}
                        </span>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
                          {r.methods.map((m) => <Badge key={m}>{m}</Badge>)}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: '2px', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
                        <span style={{ color: 'var(--text)' }}>{r.period_label}</span>
                        <span>{r.window_label}</span>
                        <span>{r.observed_at ? `Observed ${fmtDate(r.observed_at)}` : 'No observation yet'}</span>
                      </div>
                      <span
                        style={{
                          padding: '2px 10px',
                          borderRadius: '999px',
                          background: style.bg,
                          color: style.fg,
                          fontSize: 'var(--font-size-3xs)',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {style.label}
                      </span>
                      <Button
                        variant="ghost"
                        compact
                        disabled={!r.evidence_hash}
                        onClick={() => setVerify(r)}
                      >
                        Verify
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </article>
          ))}
        </div>
      ) : null}

      {verify ? (
        <EngagementVerifyModal row={verify} onClose={() => setVerify(null)} />
      ) : null}
    </section>
  );
}

function EngagementVerifyModal({ row, onClose }: { row: EngagementEvidenceRow; onClose: () => void }) {
  const dialogRef = useDialogMobileOnly<HTMLDivElement>(true);
  async function copy(v: string) {
    try { await navigator.clipboard.writeText(v); } catch { /* clipboard unavailable */ }
  }
  return (
    <div
      ref={dialogRef}
      aria-label="Verify evidence"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay, rgba(0,0,0,0.5))',
        display: 'grid', placeItems: 'center', padding: 'var(--space-4)', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)', padding: 'var(--space-4)',
          display: 'grid', gap: 'var(--space-3)', maxWidth: '520px', width: '100%',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>Verify evidence</h2>
          <Button variant="ghost" compact onClick={onClose} aria-label="Close verify dialog">Close</Button>
        </div>
        <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          {row.test_title} on {row.asset_name} — {row.period_label}.
        </p>
        <div style={{ display: 'grid', gap: '2px' }}>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Evidence hash</span>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <code style={{
              flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)',
              padding: 'var(--space-2)', background: 'var(--surface-alt, var(--surface-elevated))',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))',
              color: 'var(--text)', overflowWrap: 'anywhere',
            }}>{row.evidence_hash ?? '—'}</code>
            {row.evidence_hash ? (
              <Button variant="ghost" compact onClick={() => copy(row.evidence_hash!)} aria-label="Copy evidence hash">Copy</Button>
            ) : null}
          </div>
        </div>
        {row.check_id ? (
          <div style={{ display: 'grid', gap: '2px' }}>
            <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Check id</span>
            <code style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)',
              padding: 'var(--space-2)', background: 'var(--surface-alt, var(--surface-elevated))',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))',
              color: 'var(--text)', overflowWrap: 'anywhere',
            }}>{row.check_id}</code>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LibraryWrap({ collapsed, count, children }: { collapsed: boolean; count: number | null; children: ReactNode }) {
  if (!collapsed) return <>{children}</>;
  return (
    <details className="gp-ev-all" data-part="all-evidence">
      <summary>{count !== null ? `All evidence · ${count}` : 'All evidence'}</summary>
      <div style={{ display: 'grid', gap: 'var(--space-3)', paddingTop: 'var(--space-3)' }}>{children}</div>
    </details>
  );
}

function StatusBadgeChip({ status }: { status: string }) {
  const isPass = status === 'pass';
  const isFail = status === 'fail';
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: '999px',
        background: isPass ? 'var(--success-bg)' : isFail ? 'var(--danger-bg)' : 'var(--surface-alt)',
        color: isPass ? 'var(--success-fg)' : isFail ? 'var(--danger-fg)' : 'var(--text-dim)',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 500,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  );
}

/** PR A r3: ?item=<id> (a cited result's evidence link) scrolls to and focuses that row. */
function UnifiedEvidenceCard({ item, focused }: { item: UnifiedEvidenceItem; focused?: boolean }) {
  const rowStyle = {
    border: `1px solid ${focused ? 'var(--focus-ring)' : 'var(--border)'}`,
    borderRadius: 'var(--radius-card)',
    background: 'var(--surface)',
    padding: 'var(--space-3)',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: 'var(--space-1)',
  } as const;
  if (item.source_type === 'connector') {
    return (
      <li id={`ev-${item.id}`} tabIndex={-1} data-focused={focused || undefined} style={rowStyle}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text)', fontWeight: 500, fontFamily: 'var(--font-mono)' }}>
            {item.control_id}
          </span>
          {item.status ? <StatusBadgeChip status={item.status} /> : null}
          <Badge>{item.connector_kind ?? 'connector'}</Badge>
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '999px',
              background: 'var(--surface-alt)',
              color: 'var(--text-dim)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            connector
          </span>
        </div>
        <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
          Observed {fmtDate(item.timestamp)}
          {item.evidence_hash ? (
            <span>
              {' '}
              <span style={{ fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }} title={item.evidence_hash}>{item.evidence_hash}</span>
            </span>
          ) : null}
        </div>
      </li>
    );
  }

  // Manual evidence
  return (
    <li id={`ev-${item.id}`} tabIndex={-1} data-focused={focused || undefined} style={rowStyle}>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text)', fontWeight: 500 }}>{item.title}</span>
        <Badge>{item.kind}</Badge>
        {item.file_name ? (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', overflowWrap: 'anywhere' }}>
            {item.file_name}
          </span>
        ) : null}
        <span
          style={{
            padding: '2px 8px',
            borderRadius: '999px',
            background: 'var(--surface-alt)',
            color: 'var(--text-dim)',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 500,
            textTransform: 'uppercase',
          }}
        >
          manual
        </span>
      </div>
      <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
        Uploaded {fmtDate(item.timestamp)}
      </div>
    </li>
  );
}

function NewLinkForm({ slug, onCreated }: { slug: string; onCreated: () => void }) {
  const [type, setType] = useState<EvidenceType>('monitoring');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [capturedAt, setCapturedAt] = useState('');
  const [nextReviewAt, setNextReviewAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await req<unknown>(`/api/w/${encodeURIComponent(slug)}/evidence`, {
        method: 'POST',
        body: JSON.stringify({
          type,
          title,
          artifact_ref: url,
          artifact_ref_kind: 'url',
          description: description || undefined,
          version_label: versionLabel || undefined,
          captured_at: capturedAt || undefined,
          next_review_at: nextReviewAt || undefined,
        }),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add evidence');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>Add link evidence</h2>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="link-type" label="Type">
        <Select id="link-type" value={type} onChange={(e) => setType(e.target.value as EvidenceType)}>
          <option value="monitoring">Monitoring (dashboard, log query)</option>
          <option value="technical">Technical control</option>
          <option value="process">Process record</option>
          <option value="freeform">Note</option>
        </Select>
      </Field>
      <Field id="link-title" label="Title">
        <input
          id="link-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="ML Ops fairness dashboard"
          style={{
            width: '100%',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt, var(--surface))',
            color: 'var(--text)',
          }}
        />
      </Field>
      <Field id="link-url" label="URL">
        <input
          id="link-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://grafana.example.com/d/fairness"
          style={{
            width: '100%',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt, var(--surface))',
            color: 'var(--text)',
          }}
        />
      </Field>
      <Field id="link-version" label="Version label (optional)">
        <input
          id="link-version"
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          placeholder="2026-Q2"
          style={{
            width: '100%',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt, var(--surface))',
            color: 'var(--text)',
          }}
        />
      </Field>
      {type === 'monitoring' ? (
        <Field id="link-captured" label="Captured at (optional)">
          <input
            id="link-captured"
            type="datetime-local"
            value={capturedAt}
            onChange={(e) => setCapturedAt(e.target.value ? new Date(e.target.value).toISOString() : '')}
            style={{
              width: '260px',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-input)',
              border: '1px solid var(--border)',
              background: 'var(--surface-alt, var(--surface))',
              color: 'var(--text)',
            }}
          />
        </Field>
      ) : null}
      <Field id="link-next-review" label="Next review at (optional)">
        <input
          id="link-next-review"
          type="date"
          value={nextReviewAt}
          onChange={(e) => setNextReviewAt(e.target.value)}
          style={{
            width: '260px',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt, var(--surface))',
            color: 'var(--text)',
          }}
        />
      </Field>
      <Field id="link-description" label="Description (optional)">
        <Textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={submit} disabled={saving || !title || !url}>
          {saving ? 'Adding…' : 'Add artifact'}
        </Button>
      </div>
    </section>
  );
}

/**
 * V109-1-19 R3 (R2-H3 / Medium): evidence for ONE objective (the objective upload, which writes its test)
 * or for ONE test GAC Pro couldn't check (the library upload, linked to the test). Pre-filled — the person
 * only picks the file. PDF, Word, Markdown, text, PNG or JPG. The confirmation says when it counts.
 */
const ATTACH_ACCEPT = ['.pdf', '.doc', '.docx', '.md', '.txt', '.png', '.jpg', '.jpeg'];
function AttachEvidence({ slug, testKey, objective, forLabel, returnTo }: {
  slug: string; testKey: string | null; objective: { id: string; program: string; periodId: string } | null; forLabel: string | null; returnTo: string | null;
}) {
  const invalidate = useInvalidate();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [fileErr, setFileErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const label = forLabel ?? (objective ? 'this objective' : 'the test GAC Pro couldn’t check');
  if (objective) {
    return (
      <section id="attach" className="ev-attach" aria-label={`Add evidence for ${label}`} data-part="attach-evidence">
        <h2 className="ev-attach-h">{`Add evidence for ${label}`}</h2>
        <UploadForObjective slug={slug} program={objective.program} periodId={objective.periodId} refLabel={label} objectives={[{ id: objective.id, ref: label }]}
          testKey={testKey} startOpen forLabel={label} label={`Upload for ${label}`} />
        {returnTo ? <Link to={returnTo}>Back</Link> : null}
      </section>
    );
  }
  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (busy || !testKey) return;
    if (!file) { setFileErr('Choose a file.'); document.getElementById('attach-file')?.focus(); return; }
    setBusy(true); setError(null);
    try {
      await uploadEvidenceForTest(slug, { testKey, file, title: title.trim() || file.name });
      invalidate('evidence:changed', slug);
      setFile(null); setTitle('');
      setDone(`Uploaded ${title.trim() || file.name}. It clears “Couldn't check” at the next check.`);
    } catch (err) { setError(err instanceof Error ? err.message : 'The upload failed. Try again.'); }
    finally { setBusy(false); }
  };
  return (
    <section id="attach" className="ev-attach" aria-labelledby="attach-h" data-part="attach-evidence">
      <h2 id="attach-h" className="ev-attach-h">{`Attach evidence for ${label}`}</h2>
      <p className="ev-attach-note">Linked to the test GAC Pro couldn’t check, so it counts from the next check.</p>
      <p role="status" className="ev-attach-done" data-part="attach-done">{done}</p>
      <form onSubmit={submit} noValidate className="ev-attach-form">
        {error ? <Alert variant="error">{error}</Alert> : null}
        <Field id="attach-file" label="File" required error={fileErr ?? undefined} hint="PDF, Word, Markdown, text, PNG or JPG · up to 25 MB">
          <Input type="file" accept={ATTACH_ACCEPT.join(',')} onChange={(e) => {
            const f = e.currentTarget.files?.[0] ?? null;
            setFileErr(f && !ATTACH_ACCEPT.some((x) => f.name.toLowerCase().endsWith(x)) ? 'Choose a PDF, Word, Markdown, text, PNG or JPG file.' : null);
            setFile(f && ATTACH_ACCEPT.some((x) => f.name.toLowerCase().endsWith(x)) ? f : null);
          }} />
        </Field>
        <Field id="attach-title" label="Title">
          <Input value={title} maxLength={200} onChange={(e) => setTitle(e.currentTarget.value)} />
        </Field>
        <div className="ev-attach-acts">
          <Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Uploading…' : 'Attach evidence'}</Button>
          {returnTo ? <Link to={returnTo}>Back</Link> : null}
        </div>
      </form>
    </section>
  );
}

function NewUploadForm({ slug, onCreated, forLabel }: { slug: string; onCreated: () => void; forLabel?: string | null }) {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [reviewCadence, setReviewCadence] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('type', 'policy');
      form.set('title', title);
      form.set('file', file);
      if (description) form.set('description', description);
      if (versionLabel) form.set('version_label', versionLabel);
      if (reviewCadence) form.set('own_review_cadence_months', reviewCadence);

      const res = await fetch(`/api/w/${encodeURIComponent(slug)}/evidence/upload`, { // raw-fetch-ok: FormData multipart upload
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { detail?: string };
        setError(body.detail ?? `HTTP ${res.status}`);
        return;
      }
      onCreated();
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>Upload policy artifact</h2>
      {forLabel ? <p style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }} data-part="upload-for-label">{`Mock audit · ${forLabel}`}</p> : null}
      <p style={{ margin: 0, color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
        PDF, DOCX, MD, or TXT up to 25 MB.
      </p>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="upload-title" label="Title">
        <input
          id="upload-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Fairness Testing Methodology"
          style={{
            width: '100%',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt, var(--surface))',
            color: 'var(--text)',
          }}
        />
      </Field>
      <Field id="upload-file" label="File">
        <input
          id="upload-file"
          type="file"
          accept=".pdf,.docx,.md,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown,text/plain"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </Field>
      <Field id="upload-version" label="Version label (optional)">
        <input
          id="upload-version"
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          placeholder="v3.2"
          style={{
            width: '100%',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt, var(--surface))',
            color: 'var(--text)',
          }}
        />
      </Field>
      <Field id="upload-cadence" label="Own review cadence months (optional)">
        <input
          id="upload-cadence"
          type="number"
          min={1}
          max={60}
          value={reviewCadence}
          onChange={(e) => setReviewCadence(e.target.value)}
          placeholder="12"
          style={{
            width: '160px',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border)',
            background: 'var(--surface-alt, var(--surface))',
            color: 'var(--text)',
          }}
        />
      </Field>
      <Field id="upload-description" label="Description (optional)">
        <Textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" onClick={submit} disabled={saving || !file || !title}>
          {saving ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
    </section>
  );
}

// ============================================================
// NudgeBanner — overdue attestation nudges (E6).
// ============================================================
// Surfaces evidence_nudges rows written by the daily cron. Each row
// deep-links to the register item's Evidence section so the user can
// attest in place. Writers get a dismiss button; viewers see the list
// but can't clear rows (the cron will re-file next ISO week either way,
// so dismissing is a "seen it" state, not a permanent silence).

interface NudgeRow {
  nudge_id: string;
  binding_id: string;
  week_key: string;
  generated_at: string;
  risk_item_id: string;
  risk_item_title: string;
  artifact_title: string;
  artifact_type: 'policy' | 'technical' | 'monitoring' | 'process' | 'freeform';
  system_id: string | null;
  system_name: string | null;
  attestation_cadence_months: number;
  last_attested_at: string | null;
}

function NudgeBanner({ slug, canDismiss }: { slug: string; canDismiss: boolean }) {
  const [nudges, setNudges] = useState<NudgeRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setError(null);
    try {
      const body = await req<{ nudges: NudgeRow[] }>(
        `/api/w/${encodeURIComponent(slug)}/evidence/nudges`
      );
      setNudges(body.nudges);
    } catch {
      // Silent — the banner just doesn't render.
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function dismiss(nudge_id: string) {
    setDismissing((prev) => new Set(prev).add(nudge_id));
    try {
      await req<unknown>(
        `/api/w/${encodeURIComponent(slug)}/evidence/nudges/${encodeURIComponent(nudge_id)}/dismiss`,
        { method: 'POST' }
      );
      setNudges((prev) => (prev ?? []).filter((n) => n.nudge_id !== nudge_id));
    } catch {
      // Silent — nudge stays visible on failure.
    } finally {
      setDismissing((prev) => {
        const next = new Set(prev);
        next.delete(nudge_id);
        return next;
      });
    }
  }

  if (error || !nudges || nudges.length === 0) return null;

  return (
    <section
      style={{
        border: '1px solid var(--warning-fg)',
        background: 'var(--warning-bg)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
      aria-label="Overdue attestations"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--warning-text)' }}>
            {nudges.length} evidence {nudges.length === 1 ? 'row' : 'rows'} overdue for re-attestation
          </h2>
          {/* T8R2-7: the body text uses --warning-text (>= 4.5:1 on the warning surface). */}
          <p style={{ margin: 'var(--space-1) 0 0', color: 'var(--warning-text)', fontSize: 'var(--font-size-3xs)' }}>
            Click through to the register item and re-attest each binding. The daily cron re-files these next ISO week if left overdue.
          </p>
        </div>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '4px' }}>
        {nudges.slice(0, 10).map((n) => (
          <li
            key={n.nudge_id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: '4px 0',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text)',
            }}
          >
            <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Link
                to={`/w/${slug}/app/register/${encodeURIComponent(n.risk_item_id)}`}
                style={{ color: 'var(--warning-fg)', textDecoration: 'underline' }}
              >
                <span style={{ fontFamily: 'var(--font-mono)' }}>{n.risk_item_id}</span> — {n.risk_item_title}
              </Link>
              <span style={{ color: 'var(--text-dim)' }}>
                {n.artifact_title}
                {n.system_name ? ` · ${n.system_name}` : ' · profile-wide'}
                {n.last_attested_at ? ` · last attested ${fmtDate(n.last_attested_at)}` : ' · never attested'}
              </span>
            </span>
            {canDismiss ? (
              <Button
                variant="ghost"
                onClick={() => dismiss(n.nudge_id)}
                disabled={dismissing.has(n.nudge_id)}
              >
                {dismissing.has(n.nudge_id) ? 'Dismissing…' : 'Dismiss'}
              </Button>
            ) : null}
          </li>
        ))}
        {nudges.length > 10 ? (
          <li style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            …and {nudges.length - 10} more
          </li>
        ) : null}
      </ul>
    </section>
  );
}
